import { useEffect, useSyncExternalStore } from 'react';
import type { UserProfile } from '../../types';
import { fetchCompanyRows, pickCompanySettingsBlock, type CompanyConfigRow, type CompanySelf } from '../companyInvoiceConfig';
import type { Speicher } from '../verortung/sitzung';
import { haeufigkeitAktualisieren } from './haeufigkeit';
import { quelleNormalisieren } from './quelle/quelle';
import { katalogStandZuruecksetzen, overlayVersion, setzeKatalogNutzer, setzeKatalogOverlay } from './store';
import type { Gesamtkatalog, KatalogOverlay, KatalogSprache } from './typen';
import { overlayNormalisieren } from './ueberlagerung';

/**
 * Laden des Mängelkatalogs nach dem Login:
 * - Firmen-Overlay wie die Projekt-Kategorien (companyProjectCatalog.ts):
 *   Firmenblock (fetchCompanyRows + pickCompanySettingsBlock) vor eigenem
 *   Block; ist die Abfrage nicht möglich (offline), gilt der eigene Block,
 *   sonst der localStorage-Spiegel des letzten erfolgreichen Ladens.
 * - Der Gesamtkatalog für die Suche kommt per import() im Leerlauf;
 *   ein fehlgeschlagener Import (alter Chunk nach Deploy, kein Netz) setzt
 *   den Cache zurück, damit ein neuer Versuch möglich ist (Muster useCrmMap).
 *   Seit 28.09. je Sprache ein eigener Chunk und Cache ('de'/'en').
 * - Die firmenweite Häufigkeit (Reihenfolge nach Nutzung, Nr. 132) lädt
 *   parallel im Hintergrund (haeufigkeit.ts), unabhängig vom Overlay.
 * Nichts davon darf die Anmeldung aufhalten: nie werfen.
 */

export const OVERLAY_SPIEGEL_SCHLUESSEL = 'sigeko.mangelKatalog.overlay.v1';

type OverlayProfil = { id: string; name: string };

interface Spiegel {
    nutzerId: string;
    overlay: KatalogOverlay | null;
    profil: OverlayProfil | null;
}

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const feld = (o: unknown, k: string): unknown =>
    o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined;

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Vor- und Nachname, sonst E-Mail. */
function anzeigeName(o: unknown): string {
    const name = [text(feld(o, 'first_name')), text(feld(o, 'last_name'))].filter(Boolean).join(' ');
    return name || text(feld(o, 'email'));
}

/**
 * Name des Profils, dessen Block gilt. fetchCompanyRows liefert keine Namen —
 * dann bleibt die Rolle als Beschriftung.
 */
function zeilenName(row: CompanyConfigRow, profil: UserProfile): string {
    if (row.id === profil.id) return anzeigeName(profil) || 'Eigenes Profil';
    const name = anzeigeName(row);
    if (name) return name;
    return row.role === 'super_admin' ? 'Super-Admin' : 'Admin';
}

function spiegelLesen(nutzerId: string, speicher: Speicher | null): Spiegel | null {
    if (!speicher) return null;
    try {
        const roh: unknown = JSON.parse(speicher.getItem(OVERLAY_SPIEGEL_SCHLUESSEL) ?? 'null');
        if (!roh || typeof roh !== 'object' || feld(roh, 'nutzerId') !== nutzerId) return null;
        const p = feld(roh, 'profil');
        const pid = text(feld(p, 'id'));
        return {
            nutzerId,
            overlay: overlayNormalisieren(feld(roh, 'overlay')),
            profil: pid ? { id: pid, name: text(feld(p, 'name')) } : null,
        };
    } catch {
        return null;
    }
}

function spiegelSchreiben(s: Spiegel, speicher: Speicher | null): void {
    if (!speicher) return;
    try {
        if (!s.overlay) speicher.removeItem(OVERLAY_SPIEGEL_SCHLUESSEL);
        else speicher.setItem(OVERLAY_SPIEGEL_SCHLUESSEL, JSON.stringify(s));
    } catch {
        // Nur Rückfall für offline.
    }
}

/** Hochgezählt bei jedem Laden/Leeren: ein überholter Lauf setzt nichts mehr. */
let generation = 0;

/**
 * Lädt das wirksame Firmen-Overlay, setzt Nutzer und Overlay im Store und
 * startet das Vorladen der Suche. Best effort, wirft nie.
 */
export async function ladeMangelKatalog(profil: UserProfile): Promise<void> {
    const meine = ++generation;
    // Setzt jemand während der Abfrage ein Overlay (Admin-Editor), gewinnt das.
    const versionBeimStart = overlayVersion();
    try {
        const nutzerId = text(profil?.id) || null;
        setzeKatalogNutzer(nutzerId);
        void haeufigkeitAktualisieren(nutzerId, () => meine === generation);
        const speicher = standardSpeicher();

        const eigenes = overlayNormalisieren(feld(profil?.settings, 'mangelKatalog'));
        let overlay: KatalogOverlay | null = eigenes;
        let herkunft: OverlayProfil | null = eigenes && nutzerId ? { id: nutzerId, name: anzeigeName(profil) } : null;

        const self: CompanySelf = { id: profil?.id, company_name: profil?.company_name, company_id: profil?.company_id };
        const mitFirma = !!text(profil?.company_name) || !!text(profil?.company_id);
        let firmaGeladen = false;
        if (mitFirma) {
            try {
                const rows = await fetchCompanyRows();
                firmaGeladen = true;
                const gewaehlt = pickCompanySettingsBlock(rows, self, (r) => {
                    const o = overlayNormalisieren(feld(r.settings, 'mangelKatalog'));
                    return o ? { overlay: o, row: r } : undefined;
                });
                if (gewaehlt) {
                    overlay = gewaehlt.overlay;
                    herkunft = { id: gewaehlt.row.id, name: zeilenName(gewaehlt.row, profil) };
                }
            } catch (e) {
                console.warn('Firmen-Mängelkatalog nicht ladbar', e);
            }
        }
        if (meine !== generation || overlayVersion() !== versionBeimStart) return;

        if (firmaGeladen && nutzerId) {
            spiegelSchreiben({ nutzerId, overlay, profil: herkunft }, speicher);
        } else if (mitFirma && !overlay && nutzerId) {
            // Abfrage fehlgeschlagen und kein eigener Block: letzter bekannter Stand.
            const spiegel = spiegelLesen(nutzerId, speicher);
            if (spiegel?.overlay) {
                overlay = spiegel.overlay;
                herkunft = spiegel.profil;
            }
        }
        setzeKatalogOverlay(overlay, overlay ? herkunft : null);
        vorladenImLeerlauf();
    } catch (e) {
        console.warn('Mängelkatalog nicht ladbar', e);
    }
}

/**
 * Nach „Alles auf Standard“ im Admin (eigener Block gelöscht und gespeichert):
 * den wirksamen Firmenblock neu bestimmen — gilt der Katalog eines anderen
 * Admins, soll dieser Client ihn sofort zeigen statt des Standards
 * (Codex-Review 27.09.). Der Spiegel trägt noch den gelöschten eigenen Block
 * und wird deshalb vorher verworfen.
 */
export async function ladeMangelKatalogOhneEigenenBlock(profil: UserProfile): Promise<void> {
    const speicher = standardSpeicher();
    try {
        speicher?.removeItem(OVERLAY_SPIEGEL_SCHLUESSEL);
    } catch {
        // Nur Rückfall für offline.
    }
    const settings = { ...(profil?.settings ?? {}) } as Record<string, unknown>;
    delete settings.mangelKatalog;
    await ladeMangelKatalog({ ...profil, settings } as UserProfile);
}

/** Beim Abmelden: der nächste Nutzer im selben Tab erbt nichts (der Gesamtkatalog bleibt im Speicher). */
export function leereMangelKatalog(): void {
    generation++;
    try {
        katalogStandZuruecksetzen();
    } catch {
        // Abmelden darf nicht scheitern.
    }
}

// ─── Gesamtkatalog (lazy, je Sprache ein Chunk) ─────────────────────────────

interface QuellenStand {
    quelle: Gesamtkatalog | null;
    laedt: boolean;
    fehler: boolean;
}

/**
 * Je Sprache ein eigener Chunk (28.09.): feste import()-Pfade, damit der
 * Bundler beide Dateien getrennt auslagert und nur die gebrauchte lädt.
 */
const LADER: Readonly<Record<KatalogSprache, () => Promise<{ default: unknown }>>> = {
    de: () => import('./quelle/gesamtkatalog.json'),
    en: () => import('./quelle/gesamtkatalog.en.json'),
};

/** Getrennte Caches je Sprache (Stand, laufendes Promise, Vorladen geplant). */
interface QuellenCache {
    stand: QuellenStand;
    promise: Promise<Gesamtkatalog | null> | null;
    vorladenGeplant: boolean;
}

const leererCache = (): QuellenCache => ({ stand: { quelle: null, laedt: false, fehler: false }, promise: null, vorladenGeplant: false });
const caches: Record<KatalogSprache, QuellenCache> = { de: leererCache(), en: leererCache() };
const quellenZuhoerer = new Set<() => void>();

const spracheVon = (s: unknown): KatalogSprache => (s === 'en' ? 'en' : 'de');

function quellenStandSetzen(sprache: KatalogSprache, neu: QuellenStand): void {
    const c = caches[sprache];
    if (neu.quelle === c.stand.quelle && neu.laedt === c.stand.laedt && neu.fehler === c.stand.fehler) return;
    c.stand = neu;
    for (const f of [...quellenZuhoerer]) {
        try {
            f();
        } catch {
            // Ein Zuhörer darf die anderen nicht aufhalten.
        }
    }
}

// Zuletzt benutzte Sprache der Suche: nur diese lädt das Vorladen nach dem Login.
export const SUCHSPRACHE_SCHLUESSEL = 'sigeko.mangelKatalog.suchsprache.v1';
let letzteSprache: KatalogSprache | null = null;

function letzteSpracheLesen(): KatalogSprache {
    if (letzteSprache) return letzteSprache;
    try {
        letzteSprache = spracheVon(standardSpeicher()?.getItem(SUCHSPRACHE_SCHLUESSEL));
    } catch {
        letzteSprache = 'de';
    }
    return letzteSprache;
}

function letzteSpracheMerken(sprache: KatalogSprache): void {
    if (letzteSprache === sprache) return;
    letzteSprache = sprache;
    try {
        standardSpeicher()?.setItem(SUCHSPRACHE_SCHLUESSEL, sprache);
    } catch {
        // Nur Komfort fürs Vorladen.
    }
}

/**
 * Lädt den Gesamtkatalog der Sprache per import() ('de' gesamtkatalog.json,
 * 'en' gesamtkatalog.en.json). Das Promise bleibt im Modul; bei Fehler oder
 * unbrauchbarem Inhalt wird es verworfen (neuer Versuch möglich). Wirft nie,
 * liefert dann null.
 */
export function ladeGesamtkatalog(spracheRoh: KatalogSprache = 'de'): Promise<Gesamtkatalog | null> {
    const sprache = spracheVon(spracheRoh);
    const c = caches[sprache];
    if (c.stand.quelle) return Promise.resolve(c.stand.quelle);
    if (c.promise) return c.promise;
    quellenStandSetzen(sprache, { quelle: null, laedt: true, fehler: false });
    const p: Promise<Gesamtkatalog | null> = LADER[sprache]()
        .then((mod) => quelleNormalisieren(mod.default))
        .catch((e: unknown) => {
            console.warn('Gesamtkatalog nicht ladbar', e);
            return null;
        })
        .then((quelle) => {
            if (c.promise === p) c.promise = null;
            quellenStandSetzen(sprache, { quelle: quelle ?? null, laedt: false, fehler: !quelle });
            return quelle;
        });
    c.promise = p;
    return p;
}

/** Gesamtkatalog der Sprache, falls schon geladen. */
export function gesamtkatalogJetzt(sprache: KatalogSprache = 'de'): Gesamtkatalog | null {
    return caches[spracheVon(sprache)].stand.quelle;
}

function quellenAbonnieren(f: () => void): () => void {
    quellenZuhoerer.add(f);
    return () => {
        quellenZuhoerer.delete(f);
    };
}

const SCHNAPPSCHUSS: Readonly<Record<KatalogSprache, () => QuellenStand>> = {
    de: () => caches.de.stand,
    en: () => caches.en.stand,
};

const NOCHMAL: Readonly<Record<KatalogSprache, () => void>> = {
    de: () => void ladeGesamtkatalog('de'),
    en: () => void ladeGesamtkatalog('en'),
};

/**
 * Gesamtkatalog der Sprache für die Suche; startet das Laden, falls noch nicht
 * geschehen, und merkt die Sprache fürs Vorladen beim nächsten Login.
 */
export function useGesamtkatalog(spracheRoh: KatalogSprache = 'de'): {
    quelle: Gesamtkatalog | null;
    laedt: boolean;
    fehler: boolean;
    nochmal: () => void;
} {
    const sprache = spracheVon(spracheRoh);
    const s = useSyncExternalStore(quellenAbonnieren, SCHNAPPSCHUSS[sprache], SCHNAPPSCHUSS[sprache]);
    useEffect(() => {
        letzteSpracheMerken(sprache);
        const c = caches[sprache];
        if (!c.stand.quelle && !c.promise) void ladeGesamtkatalog(sprache);
    }, [sprache]);
    return { quelle: s.quelle, laedt: s.laedt, fehler: s.fehler, nochmal: NOCHMAL[sprache] };
}

/**
 * Lädt den Gesamtkatalog im Leerlauf vor (requestIdleCallback, sonst nach
 * 1,5 s) — ohne Angabe nur den der zuletzt benutzten Sprache (sonst 'de').
 */
export function vorladenImLeerlauf(spracheRoh?: KatalogSprache): void {
    const sprache = spracheRoh === undefined ? letzteSpracheLesen() : spracheVon(spracheRoh);
    const c = caches[sprache];
    if (c.vorladenGeplant || c.stand.quelle || c.promise) return;
    c.vorladenGeplant = true;
    const los = () => {
        c.vorladenGeplant = false;
        void ladeGesamtkatalog(sprache);
    };
    try {
        const w: (Window & typeof globalThis) | undefined = typeof window !== 'undefined' ? window : undefined;
        if (w && typeof w.requestIdleCallback === 'function') w.requestIdleCallback(los, { timeout: 10000 });
        else setTimeout(los, 1500);
    } catch {
        c.vorladenGeplant = false;
    }
}
